import { useEffect, useRef, useState } from "react"
import { Alert, Box, Button, Stack, TextField, Typography } from "@mui/material"
import { skipToken } from "@reduxjs/toolkit/query"
import { CheckboxElement, type UseFormReturn } from "react-hook-form-mui"
import { validate as isUuid } from "uuid"
import equal from "fast-deep-equal"
import {
  RtkHookForm,
  SKIP_FORM_SUBMIT,
} from "@core/components/form/rtkHookForm"
import type { Placement } from "../../interface/schedule"
import type { TemplateWriteAccess } from "../../hooks/useVersionDraft"
import {
  useRevisionConflict,
  type RevisionSaveAttempt,
} from "../../hooks/useRevisionConflict"
import {
  useCreatePlacementMutation,
  useLazyGetPlacementQuery,
  usePatchPlacementMutation,
} from "../../services/publications/placementsApi"
import {
  isChannelId,
  newMoscowRule,
  placementDraft,
  placementDraftPatch,
  scheduleFromDraft,
  type PlacementDraft,
} from "../../utils/placementDraft"
import { FeatureErrors } from "../common/featureErrors"
import { RevisionConflictDialog } from "../common/revisionConflictDialog"
import { ScheduleEditor } from "./scheduleEditor"
import { useGetPublicationQuery } from "../../services/publications/publicationsApi"
import { useGetTemplateVersionQuery } from "../../services/publications/templatesApi"
import { PublicationContentView } from "../publications/publicationContentView"
import { PostBotEditorLayout } from "../common/postBotPageLayout"
import {
  PlacementChannelSelect,
  PlacementPublicationSelect,
} from "./placementResources"

export interface PlacementFormProps extends TemplateWriteAccess {
  initial?: Placement
  channelId?: string
  publicationId?: string
  onSaved?: (placement: Placement) => void
}
export function PlacementForm(props: PlacementFormProps) {
  return (
    <PlacementIdentity
      key={
        props.initial?.id ??
        `new:${props.channelId ?? ""}:${props.publicationId ?? ""}`
      }
      {...props}
    />
  )
}
function PlacementIdentity(props: PlacementFormProps) {
  const [saved, setSaved] = useState<Placement>()
  return (
    <PlacementEditor
      key={saved?.id ?? "initial"}
      {...props}
      initial={saved ?? props.initial}
      onSaved={(value) => {
        if (!props.initial) setSaved(value)
        props.onSaved?.(value)
      }}
    />
  )
}
function PlacementEditor({
  initial,
  channelId,
  publicationId,
  readOnly,
  canWrite,
  onSaved,
}: PlacementFormProps) {
  const [seed] = useState(() =>
    placementDraft(
      initial ?? {
        id: "new",
        publicationId: publicationId ?? "",
        channelId: channelId ?? "",
        schedule: { schedules: [newMoscowRule()] },
        enabled: true,
        pin: false,
        deletePrevious: false,
        revision: 1,
        createdAt: "",
        updatedAt: "",
        deletedAt: null,
      },
    ),
  )
  const creating = !initial
  const [fetchRemote] = useLazyGetPlacementQuery()
  const conflict = useRevisionConflict({
    resourceKey: `placement:${seed.id}`,
    initial: seed,
    fields: ["channelId", "schedule", "enabled", "pin", "deletePrevious"],
    fetchRemote: async () =>
      placementDraft(await fetchRemote(seed.id, false).unwrap()),
  })
  const conflictRef = useRef(conflict)
  conflictRef.current = conflict
  const [context, setContext] = useState<UseFormReturn<PlacementDraft>>()
  const attempt = useRef<RevisionSaveAttempt<PlacementDraft> | null>(null)
  const mounted = useRef(false)
  const [error, setError] = useState<unknown>()
  const [validation, setValidation] = useState("")
  const [conflictOpen, setConflictOpen] = useState(false)
  const { state } = conflict
  const selectedId = state.draft.publicationId
  const publicationQuery = useGetPublicationQuery(selectedId || skipToken)
  const publication =
    selectedId &&
    !publicationQuery.isFetching &&
    !publicationQuery.error &&
    publicationQuery.currentData?.id === selectedId
      ? publicationQuery.currentData
      : undefined
  const versionQuery = useGetTemplateVersionQuery(
    publication?.templateVersionId || skipToken,
  )
  const version =
    publication &&
    !versionQuery.isFetching &&
    !versionQuery.error &&
    versionQuery.currentData?.id === publication.templateVersionId
      ? versionQuery.currentData
      : undefined
  const editable =
    !readOnly &&
    canWrite !== false &&
    state.status !== "saving" &&
    state.status !== "conflict" &&
    !initial?.deletedAt
  const dirty = !equal(state.baseline, state.draft)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])
  useEffect(() => {
    if (!context) return
    const sync = () => conflictRef.current.setDraft(context.getValues())
    sync()
    const subscription = context.watch(sync)
    return () => subscription.unsubscribe()
  }, [context])
  return (
    <>
      <PostBotEditorLayout
        preview={
          !selectedId ? (
            <Typography color="text.secondary" sx={{ p: 3 }}>
              Выберите публикацию для предпросмотра
            </Typography>
          ) : publicationQuery.isFetching ? (
            <Typography role="status">Загрузка публикации…</Typography>
          ) : publicationQuery.error ? (
            <Alert severity="warning">
              Не удалось загрузить публикацию{" "}
              <Button onClick={() => void publicationQuery.refetch()}>
                Повторить загрузку публикации
              </Button>
            </Alert>
          ) : !publication ? (
            <Alert severity="info">Публикация недоступна</Alert>
          ) : versionQuery.isFetching ? (
            <Typography role="status">Загрузка версии…</Typography>
          ) : versionQuery.error ? (
            <Alert severity="warning">
              Не удалось загрузить версию{" "}
              <Button onClick={() => void versionQuery.refetch()}>
                Повторить загрузку версии
              </Button>
            </Alert>
          ) : !version ? (
            <Alert severity="info">Версия недоступна</Alert>
          ) : (
            <PublicationContentView
              key={`${publication.id}:${version.id}`}
              version={version}
              bindings={publication.bindings}
            />
          )
        }
      >
        <RtkHookForm<PlacementDraft>
          mutationHook={
            creating ? useCreatePlacementMutation : usePatchPlacementMutation
          }
          formContextParams={{ defaultValues: seed }}
          initializeOnce
          resetOnSuccess={false}
          setFormContext={setContext}
          actions={[
            {
              text: "Сохранить размещение",
              type: "submit",
              variant: "contained",
              disabled: !editable || (!creating && !dirty),
            },
          ]}
          getSubmitHandlerParams={(values) => {
            if (!editable) return SKIP_FORM_SUBMIT
            try {
              const patch = creating
                ? undefined
                : placementDraftPatch(state.baseline, values)
              if (patch === null) return SKIP_FORM_SUBMIT
              if (
                !isChannelId(values.channelId) ||
                (creating && !isUuid(values.publicationId))
              )
                throw new Error("Выберите публикацию и канал")
              const body = creating
                ? {
                    publicationId: values.publicationId,
                    channelId: values.channelId,
                    schedule: scheduleFromDraft(values.schedule),
                    enabled: values.enabled,
                    pin: values.pin,
                    deletePrevious: values.deletePrevious,
                  }
                : undefined
              setValidation("")
              setError(undefined)
              attempt.current = conflict.beginSave(structuredClone(values))
              if (!attempt.current) return SKIP_FORM_SUBMIT
              return creating ? body! : { id: seed.id, body: patch! }
            } catch (failure) {
              setValidation(
                failure instanceof Error
                  ? failure.message
                  : "Некорректное расписание",
              )
              return SKIP_FORM_SUBMIT
            }
          }}
          afterSubmit={(saved: Placement) => {
            if (!mounted.current) return
            if (
              (!creating && saved.id !== seed.id) ||
              !saved.id ||
              !Number.isSafeInteger(saved.revision) ||
              saved.revision < 1
            )
              throw new Error("Invalid placement response")
            const draft = placementDraft(saved)
            if (!creating) attempt.current?.succeed(draft)
            context?.reset(draft)
            onSaved?.(saved)
          }}
          onSubmitError={async (failure) => {
            if (!mounted.current) return
            setError(failure)
            await attempt.current?.fail(failure)
            if (mounted.current) setConflictOpen(true)
          }}
        >
          <Typography variant="subtitle2">Публикация</Typography>
          {creating ? (
            <PlacementPublicationSelect
              value={state.draft.publicationId}
              onChange={(id) =>
                context?.setValue("publicationId", id, { shouldDirty: true })
              }
              disabled={!editable}
              selected={publication}
            />
          ) : (
            <TextField
              label="Публикация"
              size="small"
              value={publication?.name ?? "Выбранная публикация"}
              InputProps={{ readOnly: true }}
            />
          )}
          {version && (
            <Typography variant="caption" color="text.secondary">
              Версия {version.number}
            </Typography>
          )}
          <Typography variant="subtitle2">Канал</Typography>
          <Box sx={{ maxWidth: 440 }}>
            <PlacementChannelSelect
              value={state.draft.channelId}
              onChange={(id) =>
                context?.setValue("channelId", id, { shouldDirty: true })
              }
              disabled={!editable}
            />
          </Box>
          <Typography variant="subtitle2">Расписание</Typography>
          <ScheduleEditor
            value={state.draft.schedule}
            onChange={(value) =>
              context?.setValue("schedule", value, { shouldDirty: true })
            }
            readOnly={!editable}
          />
          <Typography variant="subtitle2">Параметры</Typography>
          <Stack>
            <CheckboxElement
              name="enabled"
              label="Отправлять по расписанию"
              disabled={!editable}
            />
            <CheckboxElement
              name="pin"
              label="Закреплять сообщение"
              disabled={!editable}
            />
            <CheckboxElement
              name="deletePrevious"
              label="Удалять предыдущее сообщение этого размещения"
              disabled={!editable}
            />
          </Stack>
          {validation && <Alert severity="error">{validation}</Alert>}
          <FeatureErrors error={error} />
          <FeatureErrors error={state.fetchError} />
          {state.status === "conflict" && (
            <Button onClick={() => setConflictOpen(true)}>
              Сравнить изменения
            </Button>
          )}
        </RtkHookForm>
      </PostBotEditorLayout>
      {state.status === "conflict" && (
        <RevisionConflictDialog
          open={conflictOpen}
          differences={state.differences}
          onClose={() => setConflictOpen(false)}
          onApply={(choices) => {
            const resolved = conflict.resolve(choices)
            if (resolved) context?.reset(resolved.draft)
            setConflictOpen(false)
            setError(undefined)
          }}
        />
      )}
    </>
  )
}
